import React, { useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Redirect, Link } from 'react-router-dom';
import { History } from 'history';
import { get } from 'lodash';
import {
  loginSuccess,
  loginError,
  getUserProfileSuccess,
  ILoginSuccessPayload,
} from 'actions/authenticate';
import { WeaverApiClientInstance } from 'api/weaverApiClientInstance';
import ErrorMessage from 'components/common/ErrorMessage';
import { login } from 'services/authenticate';
import { MFA_STATE, PEAR_PORTAL } from 'utils/constants';
import { getRedirectUrl } from 'utils/redirectUrlAfterLogin';
import { encodeEmail } from 'utils/normalizeData';
import { loadAuthToken, getDeviceIdCookie } from 'utils/tokenStorage';
import { routes } from 'utils/routeHelper';
import { validateEmail } from 'utils/validates';
import { LoginDTO } from '@peartherapeutics/auth-service-client';
import { AuthClientInstance } from 'api/authClientInstance';
import { AxiosError } from 'axios';
import LoginForm from './LoginForm';
import './styles.scss';

interface IOwnProps {
  history: History;
}
interface IStateProps {
  userLoggedIn: boolean;
  errorMessage: Nullable<string>;
  loginAfterIdle: boolean;
  roles: string[];
  scopes: string[];
}

interface IDispatchProps {
  loginSuccess: (payload: ILoginSuccessPayload) => void;
  loginError: (error: string) => void;
  getUserProfileSuccess: (profile: UserProfileDTO) => void;
}

export type LoginType = IOwnProps & IStateProps & IDispatchProps;

const redirectAfterLoggedIn = (loginAfterIdle: boolean, scopes: string[]) => {
  const url = getRedirectUrl(scopes);
  if (loginAfterIdle) {
    window.location.pathname = url;
    return null;
  }
  return <Redirect to={url} />;
};

export const Login = (props: LoginType) => {
  const [form, setForm] = useState({
    emailOrUsername: '',
    password: '',
  });
  const { userLoggedIn, errorMessage, loginAfterIdle, scopes } = props;
  const resolveError = (error: AxiosError) => {
    const errorMessage = error?.response?.data.message;
    const status = error?.response?.data.status;
    let message = errorMessage;
    if (status === 401 || status === 403 || !message) {
      message = 'Incorrect email, username, or password';
    }
    props.loginError(message);
  };
  const isEmail = validateEmail(form.emailOrUsername);

  const submitLoginForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      WeaverApiClientInstance.reset();
      const { emailOrUsername, password } = form;
      const trimmedEmailOrUsername = emailOrUsername.trim();
      const deviceId = getDeviceIdCookie();
      const loginDTO: LoginDTO = {
        ...(isEmail
          ? { email: trimmedEmailOrUsername }
          : { username: trimmedEmailOrUsername }),
        password,
        clientId: PEAR_PORTAL,
        deviceId,
      };
      const {
        data: { token, loginState },
      } = await AuthClientInstance.get().login({ loginDTO });
      switch (loginState) {
        case MFA_STATE.LOGGED_IN: {
          const { profile } = await login(token);
          props.getUserProfileSuccess(profile);
          props.loginSuccess({ token, email: trimmedEmailOrUsername });
          break;
        }
        case MFA_STATE.MFA_REGISTER: {
          props.history.push(routes.MFA_REGISTER, {
            loginState,
            token,
            email: trimmedEmailOrUsername,
          });
          break;
        }
        case MFA_STATE.MFA_VALIDATE: {
          props.history.push(routes.MFA_VALIDATION, {
            loginState,
            token,
            email: trimmedEmailOrUsername,
          });
          break;
        }
        default:
          break;
      }
    } catch (err) {
      // errors on login call and on profile call are different. Have to accomodate here.
      resolveError(err as AxiosError);
    }
  };

  const handleFieldChange =
    (fieldName: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setForm({
        ...form,
        [fieldName]: event.target.value,
      });
    };

  const encodedEmail = isEmail ? encodeEmail(form.emailOrUsername) : null;
  return (
    <div data-testid="login" styleName="wrapper">
      <div styleName="inside-wrapper">
        <div styleName="logo-pear" />
        <div styleName="credential-text">Enter your credentials</div>
        {userLoggedIn && redirectAfterLoggedIn(loginAfterIdle, scopes)}
        {errorMessage && (
          <ErrorMessage title="Login failed" message={errorMessage} />
        )}
        <LoginForm
          emailOrUsername={form.emailOrUsername}
          password={form.password}
          onFieldChange={handleFieldChange}
          handleSubmit={submitLoginForm}
          setError={(message: string) => props.loginError(message)}
        />
        <Link
          styleName="forgot-password"
          to={{
            pathname: routes.FORGOT_PASSWORD,
            search: encodedEmail ? `?email=${encodedEmail}` : '',
            state: { email: encodedEmail },
          }}
        >
          Forgot password?
        </Link>
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootStateType) => ({
  userLoggedIn: Boolean(state.authenticate.token || loadAuthToken()),
  errorMessage: state.authenticate.errorMessage,
  loginAfterIdle: state.authenticate.loginAfterIdle,
  roles: get(state, 'authenticate.profile.roles', []),
  scopes: get(state, 'authenticate.profile.scopes', []),
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      loginSuccess,
      loginError,
      getUserProfileSuccess,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(Login);
